/**
 * PATH: packages/quote-builder/src/components/export/ExportReadinessPanel.tsx
 * FILE_ID: QB-EXPORT-READINESS-PANEL-001
 * PURPOSE: "Ready to send" checklist card for the export editor — progress
 *          meter plus the failed blocker (red) / warning (amber) messages.
 * WHY: Convergence A5 — newmark's export editor surfaced pre-send readiness
 *      and estimators caught zero-total / empty-scope quotes before clients
 *      did; the canonical editor now gets the same guard rail.
 * FLOW:
 *   ┌─────────────────┐   ┌──────────────────────┐   ┌──────────────────┐
 *   │ ExportReadiness │ → │ progress bar + issue │ → │ estimator fixes  │
 *   │ (pure compute)  │   │ callouts             │   │ before sending   │
 *   └─────────────────┘   └──────────────────────┘   └──────────────────┘
 * RELATED:
 *   - ../../export/readiness.ts (computeExportReadiness)
 *   - ../../pages/QuoteExportPage.tsx (host; gates Send on blockers)
 */

import { AlertTriangle, CheckCircle2, OctagonAlert } from 'lucide-react'
import { cn } from '@quotech/ui'
import type { ExportReadiness } from '../../export/readiness'

export function ExportReadinessPanel({
  readiness,
  readyText,
}: {
  readiness: ExportReadiness
  readyText?: string
}) {
  const allClear = readiness.blockers.length === 0 && readiness.warnings.length === 0
  return (
    <div className="mb-4 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">Ready to send</span>
        <span
          className={cn(
            'text-[11px] font-bold',
            readiness.blockers.length > 0 ? 'text-rose-600' : allClear ? 'text-emerald-600' : 'text-amber-600',
          )}
        >
          {readiness.completedChecks}/{readiness.totalChecks}
        </span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        <div
          className={cn(
            'h-full rounded-full transition-all',
            readiness.blockers.length > 0 ? 'bg-rose-500' : allClear ? 'bg-emerald-500' : 'bg-amber-500',
          )}
          style={{ width: `${readiness.completionPct}%` }}
        />
      </div>

      {allClear ? (
        <p className="mt-2 flex items-center gap-1.5 text-[11px] text-emerald-600">
          <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" /> {readyText ?? 'All checks passed — this quote is ready to go.'}
        </p>
      ) : (
        <ul className="mt-2 space-y-1">
          {readiness.blockers.map((message) => (
            <li key={message} className="flex items-start gap-1.5 text-[11px] leading-snug text-rose-600">
              <OctagonAlert className="mt-px h-3.5 w-3.5 flex-shrink-0" />
              {message}
            </li>
          ))}
          {readiness.warnings.map((message) => (
            <li key={message} className="flex items-start gap-1.5 text-[11px] leading-snug text-amber-600">
              <AlertTriangle className="mt-px h-3.5 w-3.5 flex-shrink-0" />
              {message}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
